import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  CenterIntakeObligationRow,
  IntakeObservationInput,
  IntakeReceiptInput,
} from "./types";

interface Props {
  /** The authenticated adapter must change this key on actor/scope/page changes. */
  scopeKey: string;
  centerId: string | null;
  rows: CenterIntakeObligationRow[];
  total: number;
  view: "outstanding" | "received" | "history";
  operatorId: string;
  sourceType: string;
  operatorOptions: { id: string; name: string }[];
  onFilterChange: (filter: {
    view: "outstanding" | "received" | "history";
    operatorId: string;
    sourceType: string;
  }) => void;
  phase: "loading" | "refreshing" | "ready" | "error" | "denied";
  onRefresh: () => Promise<boolean>;
  onReceive?: (input: IntakeReceiptInput) => Promise<{
    receipts: { id: string; timing: string }[];
  }>;
  onReport?: (input: IntakeObservationInput) => Promise<void>;
  onInspectTask: (sourceTaskId: string) => void;
}

/** Shared receiving controls. The Admin adapter owns authenticated reads and
 * cache scope; the Server owns every physical receipt and observation decision. */
export function CenterIntakeReceiving(props: Props) {
  return <ReceivingPage key={props.scopeKey} {...props} />;
}

function ReceivingPage({
  rows,
  total,
  phase,
  centerId,
  onRefresh,
  onReceive,
  onReport,
  onInspectTask,
  view,
  operatorId,
  sourceType,
  operatorOptions,
  onFilterChange,
}: Props) {
  const [selected, setSelected] = useState<CenterIntakeObligationRow[]>([]);
  const [review, setReview] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [message, setMessage] = useState("");
  const [condition, setCondition] = useState<"MISSING" | "DAMAGED">("MISSING");
  const [note, setNote] = useState("");
  const [noteFeedback, setNoteFeedback] = useState<{
    failed: boolean;
    text: string;
  } | null>(null);
  const [attempt, setAttempt] = useState<IntakeObservationInput | null>(null);
  const operatorLabel = (id: string | null | undefined) =>
    id
      ? (operatorOptions.find((operator) => operator.id === id)?.name ?? id)
      : "Unknown";
  const ready = phase === "ready";
  const visible = rows;
  const detail = rows.find((row) => row.id === detailId);
  const eligible = (row: CenterIntakeObligationRow) =>
    row.status === "OPEN" && !!centerId && row.destinationCenterId === centerId;
  const changed = selected.some((old) => {
    const current = rows.find((row) => row.id === old.id);
    return (
      !current ||
      !eligible(current) ||
      old.version !== current.version ||
      [...old.packageIds].sort().join("\0") !==
        [...current.packageIds].sort().join("\0")
    );
  });
  const packages = selected.reduce((count, row) => count + row.packageCount, 0);
  const receiptDisabled = !ready || busy || refreshRequired || !onReceive;
  const resetSelection = () => {
    setSelected([]);
    setReview(false);
  };
  const refresh = async () => {
    if (lock.current) return;
    setRefreshRequired(true);
    lock.current = true;
    setBusy(true);
    try {
      if (await onRefresh()) {
        resetSelection();
        setRefreshRequired(false);
        setMessage("Goods refreshed. Reselect goods for a new confirmation.");
      } else setMessage("Refresh failed. Receipt actions remain unavailable.");
    } catch {
      setMessage("Refresh failed. Receipt actions remain unavailable.");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const confirm = async () => {
    if (
      lock.current ||
      receiptDisabled ||
      changed ||
      !centerId ||
      !selected.length ||
      !onReceive
    )
      return;
    lock.current = true;
    setBusy(true);
    const input = {
      centerId,
      items: selected.map((row) => ({
        id: row.id,
        expectedVersion: row.version,
        packageIds: [...row.packageIds],
      })),
    };
    try {
      const result = await onReceive(input);
      setMessage(
        result.receipts.some(
          (receipt) => receipt.timing === "FAILED_OR_CONFLICT"
        )
          ? "Receipt saved. Delivery timing needs review. Refresh goods before making another selection."
          : "Receipt saved. Refresh goods before making another selection."
      );
    } catch {
      setMessage(
        "Receipt unconfirmed; it may have saved or may still be processing. Refresh outstanding goods, then reselect for a new confirmation."
      );
    } finally {
      resetSelection();
      setRefreshRequired(true);
      lock.current = false;
      setBusy(false);
    }
  };
  const saveNote = async () => {
    if (
      lock.current ||
      !ready ||
      !onReport ||
      !detail?.destinationCenterId ||
      (!note.trim() && !attempt)
    )
      return;
    const input = attempt ?? {
      id: detail.id,
      requestId: crypto.randomUUID(),
      centerId: detail.destinationCenterId,
      condition,
      note: note.trim(),
    };
    lock.current = true;
    setBusy(true);
    setAttempt(input);
    try {
      await onReport(input);
      setAttempt(null);
      setNote("");
      setNoteFeedback({
        failed: false,
        text: "Problem recorded. Custody and outstanding work are unchanged.",
      });
    } catch {
      setNoteFeedback({
        failed: true,
        text: "Note unconfirmed; retry the same note to recover. No receipt was requested.",
      });
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3 p-3" aria-label="Returning to center">
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-zinc-700">
        Returning to center
      </h3>
      <p className="text-xs text-zinc-500">
        Receive goods independently of delivery planning. Open goods stay
        visible overnight.
      </p>
      {!onReceive && (
        <p className="text-xs text-zinc-500">
          Receiving actions are unavailable for this view.
        </p>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={busy || phase === "loading" || phase === "refreshing"}
        onClick={() => void refresh()}
      >
        Refresh goods
      </Button>
      {message && ready && (
        <p role="status" className="text-xs">
          {message}
        </p>
      )}
      {!ready ? (
        <p
          role={phase === "error" || phase === "denied" ? "alert" : "status"}
          className="text-xs"
        >
          {phase === "denied"
            ? "Access unavailable. Cached goods and actions are hidden."
            : phase === "error"
              ? "Goods could not be loaded. Refresh before receiving."
              : "Loading current goods…"}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <select
              aria-label="Intake view"
              className="rounded border p-2 text-xs"
              value={view}
              disabled={busy}
              onChange={(e) => {
                resetSelection();
                onFilterChange({
                  view:
                    e.target.value === "history"
                      ? "history"
                      : e.target.value === "received"
                        ? "received"
                        : "outstanding",
                  operatorId,
                  sourceType,
                });
              }}
            >
              <option value="outstanding">Outstanding</option>
              <option value="received">Received</option>
              <option value="history">Resolved history</option>
            </select>
            <select
              aria-label={
                view === "received"
                  ? "Received from operator"
                  : view === "history"
                    ? "Operator"
                    : "Returning operator"
              }
              className="min-w-0 rounded border p-2 text-xs"
              value={operatorId}
              disabled={busy}
              onChange={(e) => {
                resetSelection();
                onFilterChange({
                  view,
                  operatorId: e.target.value,
                  sourceType,
                });
              }}
            >
              <option value="">All operators</option>
              {operatorOptions.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <select
              aria-label="Intake source"
              className="rounded border p-2 text-xs"
              value={sourceType}
              disabled={busy}
              onChange={(e) => {
                resetSelection();
                onFilterChange({
                  view,
                  operatorId,
                  sourceType: e.target.value,
                });
              }}
            >
              <option value="">All sources</option>
              {[
                ["CUSTOMER_PICKUP", "Customer pickup"],
                ["CARRIER_PICKUP", "Carrier pickup"],
                ["CUSTOMER_DELIVERY", "Failed delivery"],
              ].map(([type, label]) => (
                <option key={type} value={type}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-zinc-500">
            {visible.length} loaded Shipments ·{" "}
            {visible.reduce((n, row) => n + row.packageCount, 0)} packages ·{" "}
            {total} in scope. Filters apply to authorized results; only loaded
            Shipments can be selected.
          </p>
          {!centerId && (
            <p className="text-xs">
              Select one dispatch center in the existing scope picker before
              receiving.
            </p>
          )}
          {visible.length === 0 && (
            <p className="text-xs">No matching goods on this page.</p>
          )}
          <ul className="space-y-2">
            {visible.map((row) => (
              <li
                key={row.id}
                className="rounded-xl border border-zinc-200 bg-white p-3 text-xs"
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    aria-label={`Select SHP-${row.shipmentNumber}`}
                    checked={selected.some((item) => item.id === row.id)}
                    disabled={receiptDisabled || !eligible(row)}
                    onCheckedChange={() =>
                      setSelected((old) =>
                        old.some((item) => item.id === row.id)
                          ? old.filter((item) => item.id !== row.id)
                          : [...old, row]
                      )
                    }
                  />
                  <span className="font-mono font-semibold">
                    SHP-{row.shipmentNumber}
                  </span>
                  <span>{row.packageCount} packages</span>
                </div>
                <p className="mt-1">
                  {row.destinationCenterName ??
                    row.ownerCenterName ??
                    "Center unresolved"}{" "}
                  · {row.ownerCarrierName}
                </p>
                <p>
                  {view === "received" ? (
                    <>
                      Received from:{" "}
                      {operatorLabel(
                        row.receiptHistory?.slice(-1)[0]?.priorOperatorId
                      )}
                    </>
                  ) : (
                    <>
                      Last confirmed holder:{" "}
                      {row.latestConfirmedOperatorName ?? "Unknown"}
                      {row.custodyDisputed ? " · disputed" : ""}
                    </>
                  )}
                </p>
                <p>
                  {row.status === "CLOSED" ? "Closed obligation" : row.status} ·{" "}
                  {row.reason ?? "Intake obligation"}
                </p>
                {row.blockReason && <p>{row.blockReason}</p>}
                {row.pendingTransfer && (
                  <p>Transfer awaiting {row.pendingTransfer.receiverName}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || (!!attempt && attempt.id !== row.id)}
                    onClick={() => {
                      setDetailId(row.id);
                      setNote("");
                      if (!attempt) setNoteFeedback(null);
                    }}
                  >
                    Inspect SHP-{row.shipmentNumber}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={receiptDisabled || !eligible(row)}
                    onClick={() => {
                      setSelected([row]);
                      setReview(true);
                    }}
                  >
                    Review receipt
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <Button
            size="sm"
            disabled={receiptDisabled || !selected.length || changed}
            onClick={() => setReview(true)}
          >
            Review selected ({selected.length} Shipments · {packages} packages)
          </Button>
          {changed && (
            <p role="alert" className="text-xs">
              Selected goods changed. Refresh and select again.
            </p>
          )}
          <Dialog
            open={review}
            onOpenChange={(open) => {
              if (!busy) setReview(open);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm center receipt</DialogTitle>
                <DialogDescription>
                  Receive only these whole Shipments at{" "}
                  {selected[0]?.destinationCenterName}. This does not assign the
                  next delivery.
                </DialogDescription>
              </DialogHeader>
              <ul>
                {selected.map((row) => (
                  <li key={row.id}>
                    SHP-{row.shipmentNumber} · {row.packageCount} packages ·{" "}
                    {row.lastConfirmedCustodian ?? "Unknown holder"}
                  </li>
                ))}
              </ul>
              <p>
                {selected.length} Shipments · {packages} packages
              </p>
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => setReview(false)}
              >
                Back
              </Button>
              <Button
                disabled={receiptDisabled || changed || !selected.length}
                onClick={() => void confirm()}
              >
                Confirm receipt
              </Button>
            </DialogContent>
          </Dialog>
          <Dialog
            open={!!detail}
            onOpenChange={(open) => {
              if (!open && !busy) setDetailId(null);
            }}
          >
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  SHP-{detail?.shipmentNumber} intake history
                </DialogTitle>
                <DialogDescription>
                  Confirmed custody and reported problems are separate from
                  delivery planning.
                </DialogDescription>
              </DialogHeader>
              {detail && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => onInspectTask(detail.sourceTaskId)}
                  >
                    Inspect source TSK-{detail.sourceTaskNumber}
                  </Button>
                  <h4 className="font-semibold">Operator transfer history</h4>
                  {!detail.transferHistory?.length && (
                    <p>No confirmed transfers recorded.</p>
                  )}
                  <ul>
                    {detail.transferHistory?.map((event) => (
                      <li key={event.version}>
                        {operatorLabel(event.priorOperatorId)} →{" "}
                        {operatorLabel(event.receiverOperatorId)} ·{" "}
                        {new Date(event.occurredAt).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                  <h4 className="font-semibold">Confirmed physical outcomes</h4>
                  <ul>
                    {detail.physicalHistory?.map((event) => (
                      <li key={event.version}>
                        {event.kind === "ONWARD_COLLECTED"
                          ? "Collected for onward work"
                          : "Physical work completed"}{" "}
                        ·{" "}
                        {event.receiverName ??
                          operatorLabel(event.receiverOperatorId)}{" "}
                        · {new Date(event.occurredAt).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                  <h4 className="font-semibold">Receipts</h4>
                  {!detail.receiptHistory?.length && (
                    <p>No center receipt recorded.</p>
                  )}
                  <ul>
                    {detail.receiptHistory?.map((event) => (
                      <li key={event.version}>
                        {event.packageIds.length} packages · {event.centerId} ·
                        actor{" "}
                        {event.actor?.name ??
                          event.actorCarrierUserId ??
                          "Unavailable"}{" "}
                        · {new Date(event.occurredAt).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                  {noteFeedback && (
                    <p role={noteFeedback.failed ? "alert" : "status"}>
                      {noteFeedback.text}
                    </p>
                  )}
                  <h4 className="font-semibold">Problem reports</h4>
                  <ul>
                    {detail.discrepancies?.map((event) => (
                      <li key={event.id}>
                        {event.condition}: {event.note} · {event.actor.name} ·{" "}
                        {new Date(event.occurredAt).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                  <label>
                    Condition
                    <select
                      aria-label="Condition"
                      className="ml-2 rounded border p-2"
                      disabled={busy || !!attempt}
                      value={condition}
                      onChange={(e) =>
                        setCondition(
                          e.target.value === "DAMAGED" ? "DAMAGED" : "MISSING"
                        )
                      }
                    >
                      <option value="MISSING">Missing</option>
                      <option value="DAMAGED">Damaged</option>
                    </select>
                  </label>
                  <Textarea
                    aria-label="Problem note"
                    maxLength={2000}
                    value={attempt?.note ?? note}
                    disabled={busy || !!attempt || !onReport}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <p className="text-xs text-zinc-500">
                    Recording a problem does not receive goods or change the
                    confirmed holder.
                  </p>
                  <Button
                    disabled={
                      busy ||
                      !onReport ||
                      !detail.destinationCenterId ||
                      (!note.trim() && !attempt)
                    }
                    onClick={() => void saveNote()}
                  >
                    {attempt ? "Retry same note" : "Record problem only"}
                  </Button>
                </>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </section>
  );
}
